import { Chain, createPublicClient, http } from 'viem'

import { Hash, OwnedToken } from 'types/entities'

import { ERC20 } from 'abis/ERC20'

import { errors } from 'constants/errors'
import { toChecksumAddress } from 'utils/crypto'

import { alchemy } from './alchemy'
import { covalent } from './covalent'
import { etherNumbers } from '../../constants/variables'

const services = [covalent, alchemy]

export async function fetchToken(token: Hash, owner: Hash | undefined, chain: Chain) {
  if (!owner) {
    return null
  }
  const client = createPublicClient({ chain, transport: http() })
  const contract = { address: token, abi: ERC20 } as const

  const [name, symbol, decimals, balance] = (
    await client.multicall({
      contracts: [
        { ...contract, functionName: 'name' },
        { ...contract, functionName: 'symbol' },
        { ...contract, functionName: 'decimals' },
        { ...contract, functionName: 'balanceOf', args: [owner] },
      ],
    })
  ).map((v) => v.result)

  if (!name || !symbol) {
    throw new Error(errors.validation.FETCH_TOKEN_FAILED)
  }

  return {
    name: String(name),
    symbol: String(symbol),
    balance: String(balance),
    address: toChecksumAddress(token),
    decimals: decimals ? Number(decimals) : 0,
  }
}

export async function fetchTokens(address: Hash, chain: Chain, retryAttempt = 0): Promise<OwnedToken[]> {
  try {
    if (!address) {
      return []
    }
    const fetcher = services[retryAttempt]
    const response = await fetcher(address, chain)
    return response.filter((token) => BigInt(token.balance) > etherNumbers.BG_ZERO)
  } catch (err) {
    if (retryAttempt < services.length) {
      return fetchTokens(address, chain, ++retryAttempt)
    }
    throw err
  }
}
